interface ChartPoint {
  readonly time: number;
  readonly value: number;
}

interface BuildLiveMarketCapTrajectorySeriesArgs {
  readonly seed: ReadonlyArray<ChartPoint>;
  readonly trajectory: ReadonlyArray<ChartPoint>;
  readonly cutoffSec: number;
  readonly fallbackValue: number | undefined;
}

/**
 * Join the last stable market-cap bucket to the append-only live trajectory.
 * The retained seed covers the rolling left edge; repeated timestamp pairs
 * keep every observed change vertical instead of inventing diagonal history.
 */
export function buildLiveMarketCapTrajectorySeries({
  seed,
  trajectory,
  cutoffSec,
  fallbackValue,
}: BuildLiveMarketCapTrajectorySeriesArgs): ChartPoint[] {
  let stableSeed: ChartPoint | undefined;
  for (const point of seed) {
    if (!isPositivePoint(point) || point.time > cutoffSec) continue;
    if (stableSeed === undefined || point.time > stableSeed.time) stableSeed = point;
  }
  let trajectoryPredecessor: ChartPoint | undefined;
  for (const point of trajectory) {
    if (!isPositivePoint(point) || point.time > cutoffSec) continue;
    if (trajectoryPredecessor === undefined || point.time > trajectoryPredecessor.time) {
      trajectoryPredecessor = point;
    }
  }
  const predecessor =
    trajectoryPredecessor !== undefined &&
    (stableSeed === undefined || trajectoryPredecessor.time >= stableSeed.time)
      ? trajectoryPredecessor
      : stableSeed;
  const initialPoint =
    predecessor ??
    (fallbackValue !== undefined && Number.isFinite(fallbackValue) && fallbackValue >= 0
      ? { time: cutoffSec, value: fallbackValue }
      : undefined);
  if (initialPoint === undefined) return [];

  const result: ChartPoint[] = [initialPoint];
  let previousValue = initialPoint.value;
  const livePoints: ChartPoint[] = [];
  for (const point of trajectory) {
    if (isPositivePoint(point) && point.time >= cutoffSec) livePoints.push(point);
  }
  livePoints.sort((left, right) => left.time - right.time);
  for (const point of livePoints) {
    if (point.time <= initialPoint.time) continue;
    if (point.value !== previousValue) {
      result.push({ time: point.time, value: previousValue });
    }
    result.push(point);
    previousValue = point.value;
  }
  return result;
}

function isPositivePoint(point: ChartPoint): boolean {
  return Number.isFinite(point.time) && Number.isFinite(point.value) && point.value > 0;
}
